
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

export interface CompletionSummary {
    target_output_qty?: number;
    produced_output_qty?: number;
    remaining_output_qty?: number;
    progress_percent?: number;
    entries_count?: number;
    issue_lines_count?: number;
    issue_cost_total?: number;
    labor_hours_total?: number;
    labor_cost_logged?: number;
    overhead_cost_logged?: number;
    extra_cost_logged?: number;
    pending_material_lines?: number;
    shortage_material_lines?: number;
    open_material_balance_count?: number;
    open_material_balance_qty?: number;
    open_material_balances?: Array<{
        item_code?: string | null;
        item_name?: string | null;
        line_label?: string | null;
        wo_balance_qty: number;
        base_unit_name?: string | null;
    }>;
    current_total_cost?: number;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (costs: { labor_cost: number; overhead_cost: number }) => void;
    summary?: CompletionSummary | null;
}

export function CompletionModal({ isOpen, onClose, onConfirm, summary }: Props) {
    const [labor, setLabor] = useState("0");
    const [overhead, setOverhead] = useState("0");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setLabor("0");
            setOverhead("0");
            setSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;
    const hasOpenMaterialBalance = Number(summary?.open_material_balance_count || 0) > 0;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        onConfirm({
            labor_cost: parseFloat(labor) || 0,
            overhead_cost: parseFloat(overhead) || 0
        });
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold">Complete Work Order</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    <div className="p-3 bg-blue-50 text-blue-700 rounded text-sm mb-2">
                        Review WO summary, then add final cost adjustments if needed.
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="border rounded p-2">
                            <div className="text-slate-500 uppercase">Output</div>
                            <div className="font-semibold">{Number(summary?.produced_output_qty || 0).toFixed(3)} / {Number(summary?.target_output_qty || 0).toFixed(3)}</div>
                            <div className="text-slate-500">Remaining: {Number(summary?.remaining_output_qty || 0).toFixed(3)}</div>
                        </div>
                        <div className="border rounded p-2">
                            <div className="text-slate-500 uppercase">Progress</div>
                            <div className="font-semibold">{Number(summary?.progress_percent || 0).toFixed(1)}%</div>
                            <div className="text-slate-500">Entries: {Number(summary?.entries_count || 0)}</div>
                        </div>
                        <div className="border rounded p-2">
                            <div className="text-slate-500 uppercase">Issue Cost</div>
                            <div className="font-semibold">{Number(summary?.issue_cost_total || 0).toFixed(2)}</div>
                            <div className="text-slate-500">Issue lines: {Number(summary?.issue_lines_count || 0)}</div>
                        </div>
                        <div className="border rounded p-2">
                            <div className="text-slate-500 uppercase">Labor</div>
                            <div className="font-semibold">{Number(summary?.labor_cost_logged || 0).toFixed(2)}</div>
                            <div className="text-slate-500">Hours: {Number(summary?.labor_hours_total || 0).toFixed(2)}</div>
                        </div>
                        <div className="border rounded p-2">
                            <div className="text-slate-500 uppercase">Current WO Cost</div>
                            <div className="font-semibold">{Number(summary?.current_total_cost || 0).toFixed(2)}</div>
                            <div className="text-slate-500">Overhead/Extra: {Number(summary?.overhead_cost_logged || 0).toFixed(2)} / {Number(summary?.extra_cost_logged || 0).toFixed(2)}</div>
                        </div>
                    </div>

                    {(Number(summary?.pending_material_lines || 0) > 0 || Number(summary?.shortage_material_lines || 0) > 0) ? (
                        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                            Pending material lines: {Number(summary?.pending_material_lines || 0)} | Shortage lines: {Number(summary?.shortage_material_lines || 0)}.
                        </div>
                    ) : null}

                    {hasOpenMaterialBalance ? (
                        <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                            <div className="font-semibold">Cannot complete yet: issued material balance is still open.</div>
                            <div>Total WO balance: {Number(summary?.open_material_balance_qty || 0).toFixed(3)}. Consume it in Daily Entry or return it to Store.</div>
                            {(summary?.open_material_balances || []).slice(0, 3).map((row, index) => (
                                <div key={`${row.item_code || row.item_name || index}-${index}`}>
                                    {row.item_code || row.item_name}: {Number(row.wo_balance_qty || 0).toFixed(3)} {row.base_unit_name || ''}
                                    {row.line_label ? ` (${row.line_label})` : ''}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Final Labor Adjustment</label>
                        <div className="relative">
                            <span className="absolute left-3 top-2 text-slate-500">$</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={labor}
                                onChange={(e) => setLabor(e.target.value)}
                                className="w-full pl-7 p-2 border border-slate-300 rounded focus:border-blue-500 outline-none"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Final Overhead Adjustment</label>
                        <div className="relative">
                            <span className="absolute left-3 top-2 text-slate-500">$</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={overhead}
                                onChange={(e) => setOverhead(e.target.value)}
                                className="w-full pl-7 p-2 border border-slate-300 rounded focus:border-blue-500 outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || hasOpenMaterialBalance}
                            className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                        >
                            {submitting ? 'Processing...' : 'Complete Order'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
